import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SuperSearchBar from '../Product/SuperSearchBar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import MenuItem from '@material-ui/core/MenuItem';
import Popper from '@material-ui/core/Popper';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListSubheader from '@material-ui/core/ListSubheader';
import { Divider, makeStyles } from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MenuIcon from '@material-ui/icons/Menu';
import HomeIcon from '@material-ui/icons/Home';
import SearchIcon from '@material-ui/icons/Search';
import BarChartIcon from '@material-ui/icons/BarChart';
import PeopleIcon from '@material-ui/icons/People';
import AssignmentIcon from '@material-ui/icons/Assignment';
import FolderIcon from '@material-ui/icons/Folder';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import SettingsIcon from '@material-ui/icons/Settings';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import DescriptionIcon from '@material-ui/icons/Description';
import BuildIcon from '@material-ui/icons/Build';
import PersonIcon from '@material-ui/icons/Person';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import ThumbsUpDownIcon from '@material-ui/icons/ThumbsUpDown';

import './Header.css';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import { AppContext } from '../../Context/AppContextProvide';
import { CompanyLogoContext } from '../../Context/CompanyLogoContextProvider';

const useStyles = makeStyles(() => ({
  menuPaper: {
    minWidth: 230,
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
  menuList: {
    padding: 0,
  },
  sectionHeader: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: '#aaa',
    lineHeight: '28px',
    paddingTop: 8,
    paddingBottom: 0,
    paddingLeft: 16,
  },
  item: {
    padding: '8px 16px',
    '&:hover': { backgroundColor: '#f5f5f5 !important' },
  },
  itemIcon: {
    minWidth: 32,
    color: '#666',
  },
  itemLabel: {
    fontSize: 14,
    color: '#333',
  },
  // Product Search — featured row
  featuredItem: {
    padding: '10px 16px',
    backgroundColor: '#fff8f5',
    '&:hover': { backgroundColor: '#ffe8dc !important' },
  },
  featuredIcon: {
    minWidth: 32,
    color: '#FF682C',
  },
  featuredLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: '#FF682C',
  },
  // Logout
  logoutItem: {
    padding: '8px 16px',
    '&:hover': { backgroundColor: '#ffebee !important' },
  },
  logoutIcon: {
    minWidth: 32,
    color: '#c62828',
  },
  logoutLabel: {
    fontSize: 14,
    color: '#c62828',
  },
}));

const Header = (props) => {
  const classes = useStyles();
  const { user, handleLogout, loading, setWhatsNewOpen, tourMenuOpen } = React.useContext(AppContext);
  // logoLink comes from CompanyLogoContext (fetched once there) — Header used to
  // refetch it on every menu open/close via its [open, user] effect.
  const { companyLogo, logoLink } = React.useContext(CompanyLogoContext);
  const [open, setOpen] = React.useState(false);
  const effectiveOpen = open || tourMenuOpen;
  const anchorRef = React.useRef(null);
  const [username, setUsername] = React.useState('');
  // File Processing nav is shown to hidden users always, or to any admin when the toggle
  // is on. The toggle is fetched once in App (getPageToggles) and passed down here, so
  // Header no longer issues its own duplicate getfileprocessingenabled request.
  const fileProcessingEnabled = props.fileProcessingEnabled ?? false;

  const isAdmin = user.access_type_cd === 'a';
  const isUser = user.access_type_cd === 'rw' || user.access_type_cd === 'ro';
  // Hide the banner search box on phone-sized screens.
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const history = useHistory();
  const location = useLocation();

  const handleClose = (event) => {
    if (event && anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = React.useRef(open);

  useEffect(() => {
    if (prevOpen.current === true && open === false && anchorRef.current !== null) {
      anchorRef.current.focus();
    }

    if (user.user_id) {
      setUsername(user.name === null ? user.user_name : user.name);
    }

    prevOpen.current = open;
  }, [open, user]);

  const handlePageNavigation = (path) => {
    history.push(path, { from: location });
    handleClose();
  };

  const AppHeader = (user) => {
    let absoluteLogoLink = logoLink;
    if (!logoLink.toLowerCase().startsWith('http')) {
      absoluteLogoLink = '//' + absoluteLogoLink;
    }

    return user.user_id ? (
      <div className="appbar">
        <AppBar className="header" position="fixed" style={{ zIndex: 1100 }}>
          <Toolbar>
            <a target="_blank" rel="noopener noreferrer" href={absoluteLogoLink}>
              <img className="company-logo" src={companyLogo} alt="logo" />
            </a>

            {(isUser || isAdmin) && !isSmallScreen && (
              <div
                id="tour-header-search"
                style={{
                  width: 360,
                  maxWidth: '40vw',
                  marginLeft: 24,
                  background: '#fff',
                  borderRadius: 4,
                }}
              >
                <SuperSearchBar
                  placeholder="Search products…"
                  onSearch={(q) =>
                    q && history.push(`/search?q=${encodeURIComponent(q)}`)
                  }
                />
              </div>
            )}

            <span className="spacer" />

            <Button
              ref={anchorRef}
              className="product__user-menu"
              aria-controls={open ? 'menu-list-grow' : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
            >
              <Avatar className="header__avatar" alt="profile picture" src={user.picture} />
              <MenuIcon fontSize="large" className="menu-icon" />
              <Typography className="header__username">{username}</Typography>
              <ExpandMoreIcon className="menu-expand-icon" />
            </Button>

            {/* positionFixed keeps the dropdown glued to the button in the
                position:fixed header; without it the body-portaled popper is
                placed at anchorTop + scrollY and drifts away as you scroll. */}
            <Popper open={effectiveOpen} anchorEl={anchorRef.current} role={undefined} transition style={{ zIndex: 10002 }} popperOptions={{ positionFixed: true }}>
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <Paper className={classes.menuPaper}>
                    <ClickAwayListener onClickAway={(event) => { if (!tourMenuOpen) handleClose(event); }}>
                      <MenuList
                        className={classes.menuList}
                        autoFocusItem={effectiveOpen}
                        id="menu-list-grow"
                        onKeyDown={handleListKeyDown}
                      >

                        {/* ── Featured: Product Search ── */}
                        {(isUser || isAdmin) && (
                          <MenuItem
                            className={classes.featuredItem}
                            onClick={() => handlePageNavigation('/products')}
                          >
                            <ListItemIcon className={classes.featuredIcon}>
                              <SearchIcon fontSize="small" />
                            </ListItemIcon>
                            <Typography className={classes.featuredLabel}>Product Search</Typography>
                          </MenuItem>
                        )}

                        {/* ── New parallel search (test) ── */}
                        {(isUser || isAdmin) && (
                          <MenuItem
                            className={classes.item}
                            onClick={() => handlePageNavigation('/search')}
                          >
                            <ListItemIcon className={classes.itemIcon}>
                              <SearchIcon fontSize="small" />
                            </ListItemIcon>
                            <Typography className={classes.itemLabel}>Search Beta</Typography>
                          </MenuItem>
                        )}

                        <Divider />

                        {/* ── Navigate ── */}
                        <ListSubheader className={classes.sectionHeader} disableSticky>
                          Navigate
                        </ListSubheader>

                        <MenuItem className={classes.item} onClick={() => handlePageNavigation('/home')}>
                          <ListItemIcon className={classes.itemIcon}><HomeIcon fontSize="small" /></ListItemIcon>
                          <Typography className={classes.itemLabel}>Home</Typography>
                        </MenuItem>

                        {(isUser || isAdmin) && (
                          <MenuItem className={classes.item} onClick={() => handlePageNavigation('/stats')}>
                            <ListItemIcon className={classes.itemIcon}><BarChartIcon fontSize="small" /></ListItemIcon>
                            <Typography className={classes.itemLabel}>Product Stats</Typography>
                          </MenuItem>
                        )}

                        {(isUser || isAdmin) && (
                          <MenuItem className={classes.item} onClick={() => handlePageNavigation('/lead-management')}>
                            <ListItemIcon className={classes.itemIcon}><PeopleIcon fontSize="small" /></ListItemIcon>
                            <Typography className={classes.itemLabel}>Lead Management</Typography>
                          </MenuItem>
                        )}

                        {(isUser || isAdmin) && (
                          <MenuItem className={classes.item} onClick={() => handlePageNavigation('/manage-samples')}>
                            <ListItemIcon className={classes.itemIcon}><AssignmentIcon fontSize="small" /></ListItemIcon>
                            <Typography className={classes.itemLabel}>Manage Samples</Typography>
                          </MenuItem>
                        )}

                        {(isUser || isAdmin) && (
                          <MenuItem id="tour-feature-requests" className={classes.item} onClick={() => handlePageNavigation('/feature-requests')}>
                            <ListItemIcon className={classes.itemIcon}><ThumbsUpDownIcon fontSize="small" /></ListItemIcon>
                            <Typography className={classes.itemLabel}>Feature Requests</Typography>
                          </MenuItem>
                        )}

                        {/* ── Admin ── */}
                        {isAdmin && (
                          <>
                            <Divider />
                            <ListSubheader className={classes.sectionHeader} disableSticky>
                              Admin
                            </ListSubheader>

                            <MenuItem className={classes.item} onClick={() => handlePageNavigation('/admin')}>
                              <ListItemIcon className={classes.itemIcon}><SupervisorAccountIcon fontSize="small" /></ListItemIcon>
                              <Typography className={classes.itemLabel}>Admin</Typography>
                            </MenuItem>

                            <MenuItem className={classes.item} onClick={() => handlePageNavigation('/settings')}>
                              <ListItemIcon className={classes.itemIcon}><SettingsIcon fontSize="small" /></ListItemIcon>
                              <Typography className={classes.itemLabel}>Settings</Typography>
                            </MenuItem>

                            {!!user.is_hidden && (
                              <MenuItem className={classes.item} onClick={() => handlePageNavigation('/duplicate-detection')}>
                                <ListItemIcon className={classes.itemIcon}><MergeTypeIcon fontSize="small" /></ListItemIcon>
                                <Typography className={classes.itemLabel}>Duplicate Detection</Typography>
                              </MenuItem>
                            )}

                            <MenuItem className={classes.item} onClick={() => handlePageNavigation('/importexport')}>
                              <ListItemIcon className={classes.itemIcon}><ImportExportIcon fontSize="small" /></ListItemIcon>
                              <Typography className={classes.itemLabel}>Import / Export</Typography>
                            </MenuItem>

                            {(!!user.is_hidden || fileProcessingEnabled) && (
                              <MenuItem className={classes.item} onClick={() => handlePageNavigation('/file-processing')}>
                                <ListItemIcon className={classes.itemIcon}><DescriptionIcon fontSize="small" /></ListItemIcon>
                                <Typography className={classes.itemLabel}>File Processing</Typography>
                              </MenuItem>
                            )}

                            {!!user.is_hidden && (
                              <MenuItem className={classes.item} onClick={() => handlePageNavigation('/mapping-builder')}>
                                <ListItemIcon className={classes.itemIcon}><DescriptionIcon fontSize="small" /></ListItemIcon>
                                <Typography className={classes.itemLabel}>Mapping Builder</Typography>
                              </MenuItem>
                            )}

                            {!!user.is_hidden && (
                              <MenuItem className={classes.item} onClick={() => handlePageNavigation('/internal-tools')}>
                                <ListItemIcon className={classes.itemIcon}><BuildIcon fontSize="small" /></ListItemIcon>
                                <Typography className={classes.itemLabel}>Internal Tools</Typography>
                              </MenuItem>
                            )}
                          </>
                        )}

                        {/* ── Account ── */}
                        <Divider />
                        <ListSubheader className={classes.sectionHeader} disableSticky>
                          Account
                        </ListSubheader>

                        <MenuItem className={classes.item} onClick={() => handlePageNavigation('/user')}>
                          <ListItemIcon className={classes.itemIcon}><PersonIcon fontSize="small" /></ListItemIcon>
                          <Typography className={classes.itemLabel}>Profile</Typography>
                        </MenuItem>

                        {(isUser || isAdmin) && (
                          <MenuItem className={classes.item} onClick={() => handlePageNavigation('/customer-home')}>
                            <ListItemIcon className={classes.itemIcon}><FolderIcon fontSize="small" /></ListItemIcon>
                            <Typography className={classes.itemLabel}>Your Samples</Typography>
                          </MenuItem>
                        )}

                        {(isUser || isAdmin) && (
                          <MenuItem className={classes.item} onClick={() => { setWhatsNewOpen(true); handleClose(); }}>
                            <ListItemIcon className={classes.itemIcon}><NewReleasesIcon fontSize="small" /></ListItemIcon>
                            <Typography className={classes.itemLabel}>What's New</Typography>
                          </MenuItem>
                        )}

                        {(isUser || isAdmin) && (
                          <MenuItem id="tour-help" className={classes.item} onClick={() => handlePageNavigation('/about')}>
                            <ListItemIcon className={classes.itemIcon}><HelpOutlineIcon fontSize="small" /></ListItemIcon>
                            <Typography className={classes.itemLabel}>Help</Typography>
                          </MenuItem>
                        )}

                        <MenuItem
                          className={classes.logoutItem}
                          onClick={() => { handleLogout(); handleClose(); }}
                        >
                          <ListItemIcon className={classes.logoutIcon}><ExitToAppIcon fontSize="small" /></ListItemIcon>
                          <Typography className={classes.logoutLabel}>Logout</Typography>
                        </MenuItem>

                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Toolbar>
        </AppBar>
      </div>
    ) : (
      <div className="appbar">
        <AppBar className="header" position="fixed">
          <Toolbar>
            <a target="_blank" rel="noopener noreferrer" href={logoLink}>
              <img className="company-logo" src={companyLogo} alt="logo" />
            </a>
            <span className="spacer" />
            <Button
              disabled={loading}
              className="login"
              onClick={() => {
                const path = /^(\/$)|(\/signin)|(\/auth)|(\/register)|(\/confirm-user)|(\/forgotpass)|(\/resetpass)|(\/notfound)/.test(location.pathname)
                  ? ''
                  : location.pathname;
                localStorage.setItem('returnUrl', path);
                handlePageNavigation('/signin');
              }}
            >
              Login
            </Button>
          </Toolbar>
        </AppBar>
      </div>
    );
  };

  return AppHeader(user);
};

export default Header;
